import { useState } from 'react';
import { useAgentVersions, useRollbackAgent } from '../../hooks/useAgents';
import { AgentVersionHistory, ChangeType } from '../../types/agent-version';
import { Button } from '../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { RollbackModal } from './RollbackModal';

interface VersionHistoryProps {
  agentId: string;
  agentName: string;
  currentVersion: number;
}

const changeTypeLabel: Record<ChangeType, string> = {
  CREATE: 'Created',
  UPDATE: 'Updated',
  ROLLBACK: 'Rolled back',
};

const changeTypeBadgeClass: Record<ChangeType, string> = {
  CREATE: 'bg-green-100 text-green-800',
  UPDATE: 'bg-blue-100 text-blue-800',
  ROLLBACK: 'bg-amber-100 text-amber-800',
};

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function VersionHistory({
  agentId,
  agentName,
  currentVersion,
}: VersionHistoryProps) {
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);
  const [compareVersions, setCompareVersions] = useState<[AgentVersionHistory, AgentVersionHistory] | null>(null);
  const [rollbackTarget, setRollbackTarget] = useState<AgentVersionHistory | null>(null);
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAgentVersions(agentId, { page, limit: 10 });
  const rollbackAgent = useRollbackAgent();

  const versions = data?.data || [];
  const meta = data?.meta;

  const handleRollback = (reason?: string) => {
    if (!rollbackTarget) return;
    rollbackAgent.mutate(
      { agentId, version: rollbackTarget.version, reason },
      { onSuccess: () => setRollbackTarget(null) }
    );
  };

  const toggleExpand = (versionId: string) => {
    setExpandedVersion((prev) => (prev === versionId ? null : versionId));
  };

  const handleCompare = (v: AgentVersionHistory) => {
    if (!compareVersions) {
      setCompareVersions([v, v]);
    } else if (compareVersions[0].id === v.id) {
      setCompareVersions(null);
    } else {
      setCompareVersions([compareVersions[0], v]);
    }
  };

  return (
    <>
      <Card id="version-history">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Version History</CardTitle>
              <CardDescription>
                Track changes and rollback to previous versions
              </CardDescription>
            </div>
            {compareVersions && compareVersions[0].id !== compareVersions[1].id && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCompareVersions(null)}
              >
                Clear Compare
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 bg-slate-100 rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : versions.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-8">
              No version history available.
            </p>
          ) : (
            <>
              {/* Compare diff view */}
              {compareVersions &&
                compareVersions[0].id !== compareVersions[1].id && (
                  <div className="mb-4 p-4 bg-slate-50 rounded-lg border">
                    <h4 className="text-sm font-semibold mb-2">
                      Comparing v{compareVersions[0].version} vs v{compareVersions[1].version}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Version {compareVersions[0].version}
                        </p>
                        <pre className="text-xs bg-white p-3 rounded border overflow-auto max-h-64">
                          {JSON.stringify(compareVersions[0].snapshot, null, 2)}
                        </pre>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Version {compareVersions[1].version}
                        </p>
                        <pre className="text-xs bg-white p-3 rounded border overflow-auto max-h-64">
                          {JSON.stringify(compareVersions[1].snapshot, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

              {/* Version timeline */}
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200" />
                <div className="space-y-1">
                  {versions.map((v) => {
                    const isExpanded = expandedVersion === v.id;
                    const isCurrent = v.version === currentVersion;
                    const isCompareSelected =
                      compareVersions?.[0].id === v.id;

                    return (
                      <div key={v.id} className="relative pl-10">
                        {/* Timeline dot */}
                        <div
                          className={`absolute left-2.5 top-4 w-3 h-3 rounded-full border-2 ${
                            isCurrent
                              ? 'bg-primary border-primary'
                              : 'bg-white border-slate-300'
                          }`}
                        />

                        <div
                          className={`p-3 rounded-lg border transition-colors ${
                            isExpanded ? 'bg-slate-50 border-slate-300' : 'hover:bg-slate-50'
                          } ${isCompareSelected ? 'ring-2 ring-primary/30' : ''}`}
                        >
                          {/* Header row */}
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() => toggleExpand(v.id)}
                              className="flex items-center gap-2 text-left flex-1"
                            >
                              <span className="font-mono text-sm font-semibold">
                                v{v.version}
                              </span>
                              <span
                                className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                  changeTypeBadgeClass[v.changeType]
                                }`}
                              >
                                {changeTypeLabel[v.changeType]}
                              </span>
                              {isCurrent && (
                                <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-primary/10 text-primary">
                                  Current
                                </span>
                              )}
                              <span className="text-xs text-muted-foreground ml-auto mr-2">
                                {formatDate(v.changedAt)}
                              </span>
                              <svg
                                className={`w-4 h-4 text-muted-foreground transition-transform ${
                                  isExpanded ? 'rotate-180' : ''
                                }`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                            <div className="flex items-center gap-1 ml-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => handleCompare(v)}
                              >
                                {isCompareSelected ? 'Selected' : 'Compare'}
                              </Button>
                              {!isCurrent && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => setRollbackTarget(v)}
                                >
                                  Rollback
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Change reason */}
                          {v.changeReason && (
                            <p className="text-xs text-muted-foreground mt-1 italic">
                              "{v.changeReason}"
                            </p>
                          )}

                          {/* Expanded snapshot */}
                          {isExpanded && (
                            <div className="mt-3 pt-3 border-t">
                              <h5 className="text-xs font-semibold text-muted-foreground mb-2">
                                Snapshot
                              </h5>
                              <pre className="text-xs bg-white p-3 rounded border overflow-auto max-h-64">
                                {JSON.stringify(v.snapshot, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pagination */}
              {meta && meta.pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground px-4">
                    Page {page} of {meta.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(meta.pages, p + 1))}
                    disabled={page === meta.pages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Rollback Modal */}
      <RollbackModal
        isOpen={!!rollbackTarget}
        onClose={() => setRollbackTarget(null)}
        onConfirm={handleRollback}
        agentName={agentName}
        targetVersion={rollbackTarget?.version ?? 0}
        isRollingBack={rollbackAgent.isPending}
      />
    </>
  );
}

export default VersionHistory;
